import type { Dispatch } from 'react';
import { sendChannelMessage } from 'src/modules/conversations/api';
import {
    ADD_WARBOX_MESSAGE,
    UPSERT_WARBOX_MESSAGE,
} from '../constants/actions';
import type { TWarboxMessageActionTypes } from '../types';
import { ThreadKind } from 'src/views/OmniChannel/types/conversation';

type SendMessageMedia = {
    url: string;
    contentType?: string;
};

type SendTextMessagePayload = {
    conversationId: string;
    storeId?: string;
    area?: string;
    senderPhone?: string;
    recipientPhone?: string;
    message?: string;
    media?: SendMessageMedia;
    kind?: ThreadKind;
    sentBy?: string;
    sentByName?: string;
    /** When false, note messages are sent but not added to local state (e.g. no read permission). */
    persistInState?: boolean;
};

const createClientMessageId = () =>
    `warbox-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const sendTextMessage = async (
    dispatch: Dispatch<TWarboxMessageActionTypes>,
    payload: SendTextMessagePayload
): Promise<void> => {
    const messageText = payload.message?.trim() || '';
    const hasMedia = Boolean(payload.media?.url?.trim());
    const isNote = payload.kind === 'note';
    const shouldPersistInState = payload.persistInState !== false;

    if (isNote) {
        if (!messageText) {
            throw new Error('Errors.CannotSendWsp');
        }
    } else if (!messageText && !hasMedia) {
        throw new Error('Errors.CannotSendWsp');
    }

    const clientMessageId = createClientMessageId();
    const createdAt = new Date();
    const optimisticMessage = {
        _id: clientMessageId,
        conversation: payload.conversationId,
        store: payload.storeId,
        area: { name: payload.area },
        channel: 'whatsapp',
        provider: isNote ? 'internal' : 'wsp',
        direction: 'outbound',
        kind: isNote ? 'note' : 'message',
        status: isNote ? 'sent' : 'queued',
        ...(payload.sentBy
            ? {
                  sentBy: {
                      _id: payload.sentBy,
                      name: payload.sentByName,
                  },
              }
            : {}),
        recipient: payload.recipientPhone
            ? {
                  phone: payload.recipientPhone,
                  normalizedPhone: payload.recipientPhone,
              }
            : undefined,
        content: {
            text: messageText,
            media: hasMedia
                ? [
                      {
                          url: payload.media!.url,
                          contentType: payload.media?.contentType,
                      },
                  ]
                : [],
        },
        metadata: {
            clientMessageId,
            optimistic: true,
        },
        createdAt,
    };

    if (shouldPersistInState) {
        dispatch({
            type: ADD_WARBOX_MESSAGE,
            payload: optimisticMessage as any,
        });
    }

    try {
        const result = await sendChannelMessage({
            conversationId: payload.conversationId,
            ...(isNote
                ? {
                      kind: 'note',
                      message: messageText,
                      ...(payload.sentBy ? { sentBy: payload.sentBy } : {}),
                  }
                : {
                      storeId: payload.storeId,
                      area: payload.area,
                      senderPhone: payload.senderPhone,
                      recipientPhone: payload.recipientPhone,
                      ...(messageText && { message: messageText }),
                      ...(hasMedia && { media: payload.media }),
                  }),
            clientMessageId,
        });

        const message = result?.message;

        if (message && shouldPersistInState) {
            dispatch({
                type: UPSERT_WARBOX_MESSAGE,
                payload: message,
            });
        }
    } catch (error) {
        const errorMessage = (error as Error).message;

        if (shouldPersistInState) {
            dispatch({
                type: UPSERT_WARBOX_MESSAGE,
                payload: {
                    ...optimisticMessage,
                    status: 'failed',
                    error: {
                        message: errorMessage,
                    },
                } as any,
            });
        }

        throw new Error(errorMessage);
    }
};
